import React, { useState, useEffect } from 'react';
import dashboardApi from '../services/dashboard';
import { KPI_META } from '../utils/dashboardKpis';
import './DashboardHome.css';

// ════════════════════════════════════════════════════════════════════════════
//  Accueil (refonte du 20/10/2026, maquette « Accueil CRM ») — une page qui dit
//  bonjour, puis ce qui attend la personne. Ouverte à tous : le serveur ne
//  renvoie que les indicateurs des pages auxquelles elle a déjà accès.
//
//  Règles :
//   - au plus 4 cartes « À traiter », classées par urgence ; un indicateur à
//     zéro ne prend pas de place ; au-delà de 4, une ligne « + N autres » ;
//   - un seul bloc métier (les ateliers du jour, pour qui a le Planning) ;
//   - 6 raccourcis « Mes pages », la liste complète sur demande.
//  Agrégats uniquement, aucune donnée nominative.
// ════════════════════════════════════════════════════════════════════════════

const NB_CARTES = 4;
const NB_RACCOURCIS = 6;

// Ordre d'urgence entre indicateurs « à traiter » (le premier passe devant).
// Ce qui se joue maintenant (un client au comptoir) d'abord, puis les retards,
// puis les clients mécontents, puis le flux courant.
const PRIORITE = [
  'atelier_a_recevoir',
  'file_magasin_attente',
  'litiges_en_retard',
  'satisfaction_a_traiter',
  'demandes_a_traiter',
  'leads_web_non_traites',
  'garanties_a_soumettre_7j',
  'relances_atelier',
  'gmb_avis_a_traiter',
  'jdma_avis_a_traiter',
  'reception_vip_a_traiter',
];

// Nature de ce qui attend : sert UNIQUEMENT au classement des cartes. Plus
// affichée depuis le 20/10 (décision utilisateur) : une étiquette « Clients
// mécontents » sur un chiffre qui mêle contents et mécontents ne serait pas
// fiable ; le chiffre et sa phrase disent ce qu'il en est.
const ETIQUETTE = {
  atelier_a_recevoir: { texte: 'Maintenant', ton: 'urgent' },
  file_magasin_attente: { texte: 'Maintenant', ton: 'urgent' },
  litiges_en_retard: { texte: 'En retard', ton: 'urgent' },
  garanties_a_soumettre_7j: { texte: 'En retard', ton: 'urgent' },
  relances_atelier: { texte: 'En retard', ton: 'urgent' },
};

// Pages les plus ouvertes du CRM (mesuré sur les permissions, 20/10) : ordre
// des raccourcis tant que l'usage personnel n'est pas enregistré.
const POPULARITE = [
  'leads-services', 'leads', 'planning', 'kpis', 'parking', 'or-en-cours',
  'pieces-atelier', 'retours-prospects', 'jdma', 'atelier', 'gmb-reviews',
  'litiges', 'takata', 'tour-vehicule', 'garantie', 'reception-vip',
];

// Identifiant de permission → identifiant de page du menu, quand ils diffèrent.
const PERMISSION_VERS_PAGE = { suivi_revisions: 'relances-atelier' };
const versPage = (perm) => PERMISSION_VERS_PAGE[perm] || perm.replace(/_/g, '-');

const nombre = (n) => Number(n || 0).toLocaleString('fr-FR');

// Carte qui ouvre sa page avec un filtre déjà posé, pour arriver directement
// sur les dossiers comptés par la carte (le filtre reprend la même règle).
const FILTRE_ARRIVEE = { litiges_en_retard: 'action_en_retard' };

// Sur l'accueil, la carte porte le NOM DE LA PAGE (on doit savoir d'un coup
// d'œil où l'on va) ; le libellé du KPI, quand il ne le dit pas, devient la phrase.
const LIBELLE_ACCUEIL = {
  litiges_en_retard: { page: 'Litiges', phrase: 'prochaine action dépassée ou non renseignée' },
  jdma_avis_a_traiter: { page: 'Avis JDMA', phrase: 'avis VN et APV sans recontact' },
  gmb_avis_a_traiter: { page: 'Avis Google', phrase: 'avis sans réponse' },
  demandes_a_traiter: { page: 'Demandes clients', phrase: 'demandes en attente de traitement' },
  leads_web_non_traites: { page: 'Leads Web', phrase: 'leads non traités (affectés ou non)' },
  garanties_a_soumettre_7j: { page: 'Garantie', phrase: 'dossiers facturés en 2026, à soumettre depuis plus de 7 jours' },
  relances_atelier: { page: 'Relances atelier', phrase: 'révisions en retard à relancer' },
  reception_vip_a_traiter: { page: 'Réception VIP', phrase: 'véhicules non restitués' },
};

// Carte « À traiter » : étiquette, page, grand chiffre, phrase, action.
function carte(kpi) {
  const base = KPI_META[kpi.key];
  const meta = LIBELLE_ACCUEIL[kpi.key] ? { ...base, label: LIBELLE_ACCUEIL[kpi.key].page } : base;
  let etiquette = ETIQUETTE[kpi.key] || { texte: 'À traiter', ton: 'normal' };
  let phrase = LIBELLE_ACCUEIL[kpi.key]?.phrase || base.hint;
  if (kpi.key === 'satisfaction_a_traiter') {
    const mecontents = kpi.extra?.detracteurs || 0;
    if (mecontents > 0) {
      etiquette = { texte: 'Clients mécontents', ton: 'urgent' };
      phrase = `réponses à traiter, dont ${nombre(mecontents)} client${mecontents > 1 ? 's' : ''} mécontent${mecontents > 1 ? 's' : ''} (note ≤ 6)`;
    } else {
      phrase = 'réponses à traiter';
    }
  }
  return { ...kpi, meta, etiquette, phrase };
}

function rangUrgence(c) {
  const ton = c.etiquette.ton === 'urgent' ? 0 : 1;
  const ordre = PRIORITE.indexOf(c.key);
  return ton * 100 + (ordre === -1 ? 99 : ordre);
}

function DashboardHome({ user, onNavigate, concessions = [], pageTitles = {} }) {
  const [kpis, setKpis] = useState([]);
  const [pages, setPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [toutesPages, setToutesPages] = useState(false);
  const isSuperAdmin = user.role === 'super-admin';

  // Périmètre = choix du menu (user = utilisateur effectif, 2026-10-26) : le
  // sélecteur « Périmètre » propre à l'Accueil est retiré.
  const concessionChoisie = user.concession_id || '';
  useEffect(() => { load(); }, [concessionChoisie]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const params = isSuperAdmin && concessionChoisie ? { concession_id: concessionChoisie } : undefined;
      const r = await dashboardApi.getKpis(params);
      setKpis((r.data.kpis || []).filter((k) => KPI_META[k.key]));
      setPages(r.data.pages ?? null);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const maintenant = new Date();
  const date = maintenant.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const prenom = (user.prenom || '').trim().split(/\s+/)[0] || '';

  // ── À traiter ──
  const aTraiter = kpis
    .filter((k) => KPI_META[k.key].kind === 'count' && Number(k.value) > 0)
    .map(carte)
    .sort((a, b) => rangUrgence(a) - rangUrgence(b));
  const cartes = aTraiter.slice(0, NB_CARTES);
  const autres = aTraiter.slice(NB_CARTES);

  // Satisfaction clients : une carte « À traiter » comme les autres (décision
  // utilisateur du 22/10, le bloc dédié du 21/10 est retiré).
  const nbSujets = aTraiter.length;
  const phraseJour = loading ? '' : error ? '' : nbSujets === 0
    ? 'Tout est à jour. Bonne journée !'
    : `${nbSujets} ${nbSujets > 1 ? 'sujets vous attendent' : 'sujet vous attend'} aujourd’hui.`;

  // ── Bloc métier : les ateliers aujourd'hui ──
  const atelier = kpis.find((k) => k.key === 'atelier_jour');

  // ── Mes pages ──
  const toutes = Object.keys(pageTitles).filter((id) => id !== 'home');
  const accessibles = pages == null
    ? toutes
    : [...new Set(pages.map(versPage))].filter((id) => pageTitles[id] && id !== 'home');
  const rang = (id) => { const i = POPULARITE.indexOf(id); return i === -1 ? 999 : i; };
  const mesPages = accessibles.sort((a, b) => rang(a) - rang(b)
    || pageTitles[a].label.localeCompare(pageTitles[b].label, 'fr'));
  const raccourcis = toutesPages ? mesPages : mesPages.slice(0, NB_RACCOURCIS);

  return (
    <div className="page-container dashboard-home accueil">
      <header className="acc-entete">
        <div className="acc-salut">
          <p className="acc-date">{date.charAt(0).toUpperCase() + date.slice(1)}</p>
          <h1 className="acc-bonjour">Bonjour{prenom ? ` ${prenom}` : ''}</h1>
          {phraseJour && <p className="acc-phrase">{phraseJour}</p>}
        </div>
      </header>

      <section className="acc-section" aria-labelledby="acc-t-faire">
        <h2 id="acc-t-faire" className="acc-titre-section">À traiter</h2>

        {loading && (
          <div className="acc-cartes">
            {Array.from({ length: NB_CARTES }).map((_, i) => <div key={i} className="acc-carte acc-squelette" aria-hidden="true" />)}
          </div>
        )}

        {!loading && error && (
          <div className="acc-vide">
            Impossible de charger les indicateurs.{' '}
            <button type="button" className="acc-lien" onClick={load}>Réessayer</button>
          </div>
        )}

        {!loading && !error && cartes.length === 0 && (
          <div className="acc-ajour">
            <span className="acc-ajour-coche" aria-hidden="true">✓</span>
            Rien ne vous attend : tout est à jour.
          </div>
        )}

        {!loading && !error && cartes.length > 0 && (
          <>
            <div className="acc-cartes">
              {cartes.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className="acc-carte"
                  onClick={() => onNavigate(c.meta.nav, FILTRE_ARRIVEE[c.key])}
                >
                  <span className="acc-carte-page">{c.meta.label}</span>
                  <span className="acc-chiffre">{nombre(c.value)}</span>
                  <span className="acc-carte-phrase">{c.phrase}</span>
                  <span className="acc-carte-action">Ouvrir →</span>
                </button>
              ))}
            </div>
            {autres.length > 0 && (
              <p className="acc-autres">
                <span className="acc-autres-titre">+ {autres.length} autre{autres.length > 1 ? 's' : ''} :</span>
                {autres.map((c) => (
                  <button key={c.key} type="button" className="acc-lien" onClick={() => onNavigate(c.meta.nav, FILTRE_ARRIVEE[c.key])}>
                    {c.meta.label} ({nombre(c.value)})
                  </button>
                ))}
              </p>
            )}
          </>
        )}
      </section>

      {!loading && !error && atelier && (
        <section className="acc-bloc" aria-labelledby="acc-t-atelier">
          <div className="acc-bloc-tete">
            <h2 id="acc-t-atelier" className="acc-bloc-titre">Les ateliers aujourd’hui</h2>
            <button type="button" className="acc-lien" onClick={() => onNavigate('planning')}>Planning →</button>
          </div>
          <div className="acc-mesures">
            <div className="acc-mesure"><span className="acc-mesure-chiffre">{nombre(atelier.value)}</span><span className="acc-mesure-libelle">rendez-vous</span></div>
            <div className="acc-mesure"><span className="acc-mesure-chiffre">{nombre(atelier.extra?.recus)}</span><span className="acc-mesure-libelle">clients reçus via la borne</span></div>
            <div className="acc-mesure"><span className="acc-mesure-chiffre">{nombre(atelier.extra?.en_attente)}</span><span className="acc-mesure-libelle">en attente à l’accueil</span></div>
            <div className="acc-mesure"><span className="acc-mesure-chiffre">{nombre(atelier.extra?.annules_sms)}</span><span className="acc-mesure-libelle">annulés par le client (SMS)</span></div>
          </div>
        </section>
      )}

      {mesPages.length > 0 && (
        <section className="acc-section" aria-labelledby="acc-t-pages">
          <div className="acc-section-tete">
            <h2 id="acc-t-pages" className="acc-titre-section">Mes pages</h2>
            {mesPages.length > NB_RACCOURCIS && (
              <button type="button" className="acc-lien" onClick={() => setToutesPages((v) => !v)} aria-expanded={toutesPages}>
                {toutesPages ? 'Moins de pages' : `Toutes mes pages (${mesPages.length}) →`}
              </button>
            )}
          </div>
          <div className="acc-raccourcis">
            {raccourcis.map((id) => (
              <button key={id} type="button" className="acc-raccourci" onClick={() => onNavigate(id)}>
                <span className="acc-raccourci-icone" aria-hidden="true">{pageTitles[id].icon}</span>
                {pageTitles[id].label}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default DashboardHome;
